/**
 * Balance a chemical equation by the null-space method.
 *
 * Every species is a column and every element (plus net charge) a row. An entry is the
 * number of atoms of that element in the species — positive for a reactant, negative for a
 * product — so a set of coefficients balances the equation exactly when the matrix times
 * the coefficient vector is zero. The coefficient vectors are the *null space* of the
 * matrix, found by Gauss–Jordan elimination over exact fractions (no floating point), then
 * scaled to the smallest whole numbers.
 *
 * - null space of dimension 1: one balance (unique up to scaling). If some of its entries
 *   are negative or zero, the equation balances only with those species on the other side
 *   of the arrow, or not taking part — reported rather than hidden;
 * - dimension 0: no balance exists (typically an element missing from one side);
 * - dimension ≥ 2: several independent balanced reactions are tangled together, so the
 *   coefficients are not unique.
 *
 * Charge is a row like any element, which is what lets ions and electrons (`e-`) balance.
 */

import {
  type Fraction,
  frac,
  nullSpace,
  toWholeNumbers,
} from "@/lib/tool-kit/chem-reactions/rational";
import {
  type ElementCount,
  type FormulaError,
  formulaToUnicode,
  type ParsedFormula,
  parseFormula,
} from "@/lib/tool-kit/chemistry/formula";

export interface Species {
  /** The species as typed, with coefficient and state symbol removed. */
  label: string;
  /** Null for the electron. */
  formula: ParsedFormula | null;
  electron: boolean;
  charge: number;
  counts: ElementCount[];
  side: "reactant" | "product";
  /** The state symbol typed after the formula, if any: s, l, g, aq or cr. */
  state: string | null;
  /** The coefficient typed in front of the formula (2 in `2NO2`, 0.5 in `0.5O2`), if any. */
  typedCoefficient: number | null;
}

export interface ParsedEquation {
  species: Species[];
  reactantCount: number;
  /** Elements in order of first appearance. */
  elements: string[];
  /** Any species carries a charge (or is an electron), so the charge row is meaningful. */
  hasCharge: boolean;
  /** At least one species had a coefficient typed in front, which is ignored. */
  ignoredCoefficients: boolean;
}

export type EquationErrorCode =
  | "empty"
  | "noArrow"
  | "manyArrows"
  | "emptySide"
  | "emptySpecies"
  | "tooMany"
  | "badSpecies";

export interface EquationError {
  code: EquationErrorCode;
  /** The species text, for `badSpecies` and `emptySpecies`. */
  token: string;
  /** The formula-parser error behind `badSpecies`. */
  formulaError?: FormulaError;
}

export type EquationParse =
  | { ok: true; equation: ParsedEquation }
  | { ok: false; error: EquationError };

const MAX_SPECIES = 20;
const MAX_ELEMENTS = 30;
const MAX_COEFFICIENT = 1_000_000;
const ARROW = /\s*(?:<?[-=]+>|→|⟶|⇒|⇌|⇄|↔|⟷|=)\s*/g;
const STATE_SYMBOL = /\s*\((s|l|g|aq|cr)\)\s*$/;
const LEADING_COEFFICIENT = /^\s*(\d+(?:\.\d+)?(?:\/\d+)?)\s*(?=[A-Za-z([{])/;
const ELECTRON = /^e(?:\^?[-−⁻])?$/;
const SEPARATOR_AHEAD = /[A-Z0-9([{]/;
const WHITESPACE = /\s/;

/** Split one side at the `+` signs that join species, not the ones that are charges. */
export function splitSpecies(side: string): string[] {
  const out: string[] = [];
  let current = "";
  for (let i = 0; i < side.length; i++) {
    const char = side[i];
    if (char !== "+") {
      current += char;
      continue;
    }
    const before = side[i - 1] ?? " ";
    const after = side[i + 1] ?? "";
    const joins =
      current.trim() === ""
        ? false
        : WHITESPACE.test(before) || SEPARATOR_AHEAD.test(after);
    if (joins) {
      out.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  out.push(current);
  return out;
}

interface ReadSpecies {
  species: Species;
  hadCoefficient: boolean;
}

/** "2", "0.5" or "1/2" as a number; null when it is not positive. */
function readCoefficient(text: string): number | null {
  const [top, bottom] = text.split("/");
  const value =
    bottom === undefined ? Number(top) : Number(top) / Number(bottom);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function readSpecies(
  raw: string,
  side: Species["side"]
): ReadSpecies | EquationError {
  let text = raw.trim();
  if (text === "") {
    return { code: "emptySpecies", token: "" };
  }
  const state = STATE_SYMBOL.exec(text)?.[1] ?? null;
  text = text.replace(STATE_SYMBOL, "");
  const typed = LEADING_COEFFICIENT.exec(text)?.[1] ?? null;
  const hadCoefficient = typed !== null;
  const typedCoefficient = typed === null ? null : readCoefficient(typed);
  text = text.replace(LEADING_COEFFICIENT, "").trim();
  if (ELECTRON.test(text.replace(/\s/g, ""))) {
    return {
      species: {
        label: "e⁻",
        formula: null,
        electron: true,
        charge: -1,
        counts: [],
        side,
        state,
        typedCoefficient,
      },
      hadCoefficient,
    };
  }
  const parsed = parseFormula(text);
  if (!parsed.ok) {
    return {
      code: "badSpecies",
      token: raw.trim(),
      formulaError: parsed.error,
    };
  }
  return {
    species: {
      label: formulaToUnicode(parsed.formula),
      formula: parsed.formula,
      electron: false,
      charge: parsed.formula.charge,
      counts: parsed.formula.counts,
      side,
      state,
      typedCoefficient,
    },
    hadCoefficient,
  };
}

function isError(value: ReadSpecies | EquationError): value is EquationError {
  return "code" in value;
}

function readSide(
  text: string,
  side: Species["side"]
): { species: Species[]; coefficient: boolean } | EquationError {
  const species: Species[] = [];
  let coefficient = false;
  for (const piece of splitSpecies(text)) {
    const read = readSpecies(piece, side);
    if (isError(read)) {
      return read;
    }
    species.push(read.species);
    coefficient = coefficient || read.hadCoefficient;
  }
  return { species, coefficient };
}

/** Read `H2 + O2 -> H2O` (or →, =, ⇌) into species. Never throws. */
export function parseEquation(raw: string): EquationParse {
  const text = raw.trim();
  if (text === "") {
    return { ok: false, error: { code: "empty", token: "" } };
  }
  const arrows = [...text.matchAll(ARROW)];
  if (arrows.length === 0) {
    return { ok: false, error: { code: "noArrow", token: "" } };
  }
  if (arrows.length > 1) {
    return { ok: false, error: { code: "manyArrows", token: "" } };
  }
  const [arrow] = arrows;
  const left = text.slice(0, arrow.index).trim();
  const right = text.slice((arrow.index ?? 0) + arrow[0].length).trim();
  if (left === "" || right === "") {
    return { ok: false, error: { code: "emptySide", token: "" } };
  }
  const reactants = readSide(left, "reactant");
  if ("code" in reactants) {
    return { ok: false, error: reactants };
  }
  const products = readSide(right, "product");
  if ("code" in products) {
    return { ok: false, error: products };
  }
  const species = [...reactants.species, ...products.species];
  if (species.length > MAX_SPECIES) {
    return { ok: false, error: { code: "tooMany", token: "" } };
  }
  const elements: string[] = [];
  for (const item of species) {
    for (const { symbol } of item.counts) {
      if (!elements.includes(symbol)) {
        elements.push(symbol);
      }
    }
  }
  if (elements.length > MAX_ELEMENTS) {
    return { ok: false, error: { code: "tooMany", token: "" } };
  }
  return {
    ok: true,
    equation: {
      species,
      reactantCount: reactants.species.length,
      elements,
      hasCharge: species.some((s) => s.charge !== 0),
      ignoredCoefficients: reactants.coefficient || products.coefficient,
    },
  };
}

/* ------------------------------------------------------------ the matrix */

export interface BalanceMatrix {
  /** One label per row: element symbols, then "charge" when it is used. */
  rowKeys: string[];
  /** rows × species; reactants positive, products negative. */
  cells: number[][];
}

export const CHARGE_ROW = "charge";

export function buildMatrix(equation: ParsedEquation): BalanceMatrix {
  const rowKeys = [...equation.elements];
  if (equation.hasCharge) {
    rowKeys.push(CHARGE_ROW);
  }
  const cells = rowKeys.map((key) =>
    equation.species.map((item) => {
      const sign = item.side === "reactant" ? 1 : -1;
      if (key === CHARGE_ROW) {
        return sign * item.charge + 0;
      }
      return sign * (item.counts.find((c) => c.symbol === key)?.count ?? 0) + 0;
    })
  );
  return { rowKeys, cells };
}

/* ------------------------------------------------------------- the solve */

export type BalanceOutcome =
  | {
      kind: "unique";
      /** Signed whole numbers, one per species. All positive means "balanced as written". */
      coefficients: number[];
      /** The null-space vector before clearing denominators. */
      vector: Fraction[];
    }
  | {
      kind: "none";
      /** Elements (or "charge") that cannot be matched, with the side they appear on. */
      oneSided: { key: string; side: "reactant" | "product" | "charge" }[];
    }
  | { kind: "many"; nullity: number; basis: number[][] }
  | { kind: "tooLarge" };

function toSafe(values: readonly bigint[]): number[] | null {
  const out: number[] = [];
  for (const value of values) {
    const asNumber = Number(value);
    if (Math.abs(asNumber) > MAX_COEFFICIENT) {
      return null;
    }
    out.push(asNumber);
  }
  return out;
}

/**
 * Pick the sign of a null-space vector. A balance can be read either way round, so choose
 * the one that moves the fewest species across the arrow; on a tie, make the first
 * non-zero entry positive.
 */
function shouldFlip(signs: readonly number[]): boolean {
  const negatives = signs.filter((sign) => sign < 0).length;
  const positives = signs.filter((sign) => sign > 0).length;
  if (negatives !== positives) {
    return negatives > positives;
  }
  return (signs.find((sign) => sign !== 0) ?? 1) < 0;
}

function orientWhole(vector: readonly bigint[]): bigint[] {
  const flip = shouldFlip(vector.map((cell) => Math.sign(Number(cell))));
  return flip ? vector.map((cell) => -cell) : [...vector];
}

function orientFractions(vector: readonly Fraction[]): Fraction[] {
  const flip = shouldFlip(vector.map((cell) => Math.sign(Number(cell.n))));
  return flip ? vector.map((cell) => ({ n: -cell.n, d: cell.d })) : [...vector];
}

function oneSidedKeys(
  equation: ParsedEquation,
  matrix: BalanceMatrix
): { key: string; side: "reactant" | "product" | "charge" }[] {
  const out: { key: string; side: "reactant" | "product" | "charge" }[] = [];
  for (const [index, key] of matrix.rowKeys.entries()) {
    const row = matrix.cells[index];
    const onReactants = row.some(
      (cell, col) => cell !== 0 && col < equation.reactantCount
    );
    const onProducts = row.some(
      (cell, col) => cell !== 0 && col >= equation.reactantCount
    );
    if (key === CHARGE_ROW) {
      continue;
    }
    if (onReactants !== onProducts) {
      out.push({ key, side: onReactants ? "reactant" : "product" });
    }
  }
  return out;
}

export function balance(
  equation: ParsedEquation,
  matrix: BalanceMatrix = buildMatrix(equation)
): BalanceOutcome {
  const columns = equation.species.length;
  const rows = matrix.cells.map((row) => row.map((cell) => frac(cell)));
  const basis = nullSpace(rows, columns);
  if (basis.length === 0) {
    return { kind: "none", oneSided: oneSidedKeys(equation, matrix) };
  }
  if (basis.length === 1) {
    const vector = orientFractions(basis[0]);
    const whole = toSafe(toWholeNumbers(vector));
    if (whole === null) {
      return { kind: "tooLarge" };
    }
    return { kind: "unique", coefficients: whole, vector };
  }
  const vectors: number[][] = [];
  for (const row of basis) {
    const whole = toSafe(orientWhole(toWholeNumbers(row)));
    if (whole === null) {
      return { kind: "tooLarge" };
    }
    vectors.push(whole);
  }
  return { kind: "many", nullity: basis.length, basis: vectors };
}

/* ------------------------------------------------------------ presentation */

export interface Term {
  coefficient: number;
  species: Species;
}

export interface OrientedEquation {
  left: Term[];
  right: Term[];
  /** Species whose coefficient is negative: they sit on the other side from where typed. */
  moved: Species[];
  /** Species whose coefficient is zero: they take no part in the balance. */
  idle: Species[];
}

/**
 * The equation a signed coefficient vector describes. A negative coefficient moves that
 * species to the opposite side; a zero coefficient drops it.
 */
export function orientEquation(
  equation: ParsedEquation,
  coefficients: readonly number[]
): OrientedEquation {
  const left: Term[] = [];
  const right: Term[] = [];
  const moved: Species[] = [];
  const idle: Species[] = [];
  for (const [index, item] of equation.species.entries()) {
    const value = coefficients[index];
    if (value === 0) {
      idle.push(item);
      continue;
    }
    const toLeft = (item.side === "reactant") === value > 0;
    if (value < 0) {
      moved.push(item);
    }
    (toLeft ? left : right).push({
      coefficient: Math.abs(value),
      species: item,
    });
  }
  return { left, right, moved, idle };
}

export interface AtomCheckRow {
  key: string;
  left: number;
  right: number;
}

/** Atoms (and charge) on each side of an oriented equation. */
export function atomCheck(
  equation: ParsedEquation,
  oriented: OrientedEquation
): AtomCheckRow[] {
  const keys = equation.hasCharge
    ? [...equation.elements, CHARGE_ROW]
    : equation.elements;
  const total = (terms: readonly Term[], key: string) =>
    terms.reduce((sum, { coefficient, species }) => {
      const per =
        key === CHARGE_ROW
          ? species.charge
          : (species.counts.find((c) => c.symbol === key)?.count ?? 0);
      return sum + coefficient * per;
    }, 0);
  return keys.map((key) => ({
    key,
    left: total(oriented.left, key),
    right: total(oriented.right, key),
  }));
}

/* ------------------------------------------------- a balance, for other tools */

export type BalancedEquation =
  | {
      kind: "ok";
      equation: ParsedEquation;
      /** Smallest whole-number coefficients, one per species, all positive. */
      coefficients: number[];
    }
  | { kind: "parse"; error: EquationError }
  | { kind: "unbalanced"; outcome: BalanceOutcome };

/**
 * Parse and balance an equation for a calculator that needs coefficients (stoichiometry,
 * limiting reactant, percent yield). Only a unique balance with every species on the side
 * it was typed on counts: anything else is handed back as `unbalanced` so the caller can
 * say that the Chemical Equation Balancer is the place to sort it out.
 */
export function solveBalanced(text: string): BalancedEquation {
  const parsed = parseEquation(text);
  if (!parsed.ok) {
    return { kind: "parse", error: parsed.error };
  }
  const outcome = balance(parsed.equation);
  if (
    outcome.kind === "unique" &&
    outcome.coefficients.every((value) => value > 0)
  ) {
    return {
      kind: "ok",
      equation: parsed.equation,
      coefficients: outcome.coefficients,
    };
  }
  return { kind: "unbalanced", outcome };
}

/** True when `coefficients` (which may be fractions) balance every row, within rounding. */
export function isBalancedWith(
  equation: ParsedEquation,
  coefficients: readonly number[]
): boolean {
  const matrix = buildMatrix(equation);
  return matrix.cells.every(
    (row) =>
      Math.abs(row.reduce((sum, cell, i) => sum + cell * coefficients[i], 0)) <
      1e-9
  );
}
